import { env } from '../config/env';
import { REQUEST_CACHE_MAX_ENTRIES } from '../constants/cache';

/**
 * An in-memory, time-expiring cache for API responses.
 *
 * One instance per kind of request ("namespace"), which is what keeps this fully
 * typed — a single global cache would have to store `unknown` and cast on the way
 * out. Instances are module-level singletons: the cache lives as long as the tab
 * and is deliberately never persisted, since what it holds is other users' data.
 *
 * Lifecycle of a key:
 *
 *   get()  ─ absent ──────▶ miss    caller fetches through load()
 *          ─ past expiry ──▶ miss    entry is dropped on the way out
 *          ─ else ────────▶ hit     value returned synchronously
 *
 *   load() ─ already in flight ──▶ joins that request, no second call
 *          ─ otherwise ──────────▶ fetches, then set() on success only
 */

interface CacheEntry<T> {
  value: T;
  /** Epoch millis; compared against `Date.now()` on read. */
  expiresAt: number;
}

/**
 * Every cache ever constructed, so a sign-out can empty all of them without each
 * one having to be imported and cleared by hand.
 */
const registry: Array<{ invalidate: () => void }> = [];

export class RequestCache<T> {
  private readonly entries = new Map<string, CacheEntry<T>>();

  /** Requests started but not yet settled, keyed the same way as `entries`. */
  private readonly inflight = new Map<string, Promise<T>>();

  /**
   * Bumped by `invalidate()`. A request that was already in flight when the data
   * changed underneath it must not write its now-stale result into the cache, and
   * comparing the epoch it started in against the current one is how it finds out.
   */
  private epoch = 0;

  constructor(
    /** Prefix for the dev-only trace log; not part of any key. */
    private readonly name: string,
    private readonly ttlMs: number,
    private readonly maxEntries: number = REQUEST_CACHE_MAX_ENTRIES
  ) {
    registry.push(this);
  }

  /** The cached value, or `null` on a miss. Never throws, never fetches. */
  get(key: string): T | null {
    const entry = this.entries.get(key);

    if (!entry) {
      this.trace('miss', key);
      return null;
    }

    if (entry.expiresAt <= Date.now()) {
      this.entries.delete(key);
      this.trace('expired', key);
      return null;
    }

    // Re-inserting moves the key to the end of the Map's iteration order, which is
    // what makes the eviction in set() least-recently-*used* rather than oldest-written.
    this.entries.delete(key);
    this.entries.set(key, entry);

    this.trace('hit', key);
    return entry.value;
  }

  set(key: string, value: T): void {
    this.entries.delete(key);
    this.entries.set(key, { value, expiresAt: Date.now() + this.ttlMs });

    while (this.entries.size > this.maxEntries) {
      const oldest = this.entries.keys().next();
      if (oldest.done) break;
      this.entries.delete(oldest.value);
    }
  }

  /**
   * Runs `loader` on a miss and caches what it resolves with.
   *
   * Callers asking for the same key while a request is open share that request
   * instead of starting a second one — which is what a double-mounted effect (React
   * StrictMode in dev) and two components reading the same list both do.
   *
   * A rejection is passed straight through and nothing is stored: an error must not
   * become a cached answer, and the next caller has to be free to retry.
   */
  async load(key: string, loader: () => Promise<T>): Promise<T> {
    const pending = this.inflight.get(key);
    if (pending !== undefined) {
      this.trace('join', key);
      return pending;
    }

    const startedAt = this.epoch;
    const request: Promise<T> = loader()
      .then((value) => {
        if (startedAt === this.epoch) this.set(key, value);
        return value;
      })
      .finally(() => {
        // Only if it is still ours — an invalidate() between start and settle may
        // have cleared the map and let a newer request claim this key.
        if (this.inflight.get(key) === request) this.inflight.delete(key);
      });

    this.inflight.set(key, request);
    return request;
  }

  /**
   * Drops the whole namespace. Used after a mutation, which may have changed rows
   * on pages other than the one being looked at — so the unit of invalidation is
   * the namespace, not the key.
   */
  invalidate(): void {
    this.entries.clear();
    this.inflight.clear();
    this.epoch += 1;
    this.trace('invalidate', '*');
  }

  /** Drops a single key, when the caller knows exactly what changed. */
  delete(key: string): void {
    this.entries.delete(key);
    this.inflight.delete(key);
  }

  private trace(event: string, key: string): void {
    // Dev only, and the one place this app logs to the console: hit/miss is
    // invisible from the UI otherwise — a working cache and a broken one both just
    // render the table. Keys hold page numbers and search terms, never tokens.
    if (!env.isDev) return;
    console.debug(`[cache:${this.name}] ${event} ${key}`);
  }
}

/**
 * Empties every cache. Called when the signed-in identity changes: these responses
 * were authorized for whoever was signed in when they were stored, and the next
 * account must not inherit them.
 */
export function clearRequestCaches(): void {
  for (const cache of registry) cache.invalidate();
}
